import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { Observable, tap } from 'rxjs';

export interface ImpersonatedUser {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
}

export interface ImpersonationState {
    active: boolean;
    user: ImpersonatedUser | null;
}

@Injectable({
    providedIn: 'root'
})
export class ImpersonationService {
    private http = inject(HttpClient);
    private router = inject(Router);
    private apiUrl = environment.apiUrl;

    // Signal for impersonation state
    private _impersonationState = signal<ImpersonationState>({
        active: false,
        user: null
    });

    // Public readonly accessor
    readonly impersonationState = this._impersonationState.asReadonly();

    // Computed values
    readonly isImpersonating = computed(() => this._impersonationState().active);
    readonly impersonatedUser = computed(() => this._impersonationState().user);

    /**
     * Start impersonating a user
     */
    startImpersonation(userId: string): Observable<any> {
        return this.http.post<any>(
            `${this.apiUrl}/users/${userId}/validate-impersonation`,
            {},
            { withCredentials: true }
        ).pipe(
            tap(response => {
                if (response.allowed && response.user) {
                    this._impersonationState.set({
                        active: true,
                        user: response.user
                    });

                    // Store in session storage for persistence across page refreshes
                    sessionStorage.setItem('impersonation', JSON.stringify({
                        active: true,
                        user: response.user
                    }));
                }
            })
        );
    }

    /**
     * Stop impersonating and return to admin view
     */
    exitImpersonation(): void {
        this._impersonationState.set({
            active: false,
            user: null
        });

        // Clear session storage
        sessionStorage.removeItem('impersonation');

        // Reload the current page to refresh all data as admin
        window.location.reload();
    }

    /**
     * Get the impersonated user ID for the HTTP header
     */
    getImpersonatedUserId(): string | null {
        const state = this._impersonationState();
        return state.active && state.user ? state.user.id : null;
    }

    /**
     * Restore impersonation state from session storage (on app init)
     */
    restoreImpersonationState(): void {
        const stored = sessionStorage.getItem('impersonation');
        if (stored) {
            try {
                const state = JSON.parse(stored);
                if (state.active && state.user) {
                    this._impersonationState.set(state);
                }
            } catch (e) {
                sessionStorage.removeItem('impersonation');
            }
        }
    }
}
